interface Entry<V> {
  readonly promise: Promise<V>;
  readonly controller: AbortController;
  /** Callers still waiting with a signal; one that came without a signal never leaves. */
  waiters: number;
  settled: boolean;
}

/**
 * Reads that several views share, for one session's caches: one read per key at a time,
 * kept for views that come later, up to `capacity` finished reads (the least recently
 * read go first; reads still running are never dropped, so whoever asks next shares
 * them). A failed read is not kept, so trying again reads again. A caller's signal ends
 * only its own wait; when the last caller leaves before the read is done, the read is
 * aborted and forgotten. `clear` forgets everything and aborts what still runs; a read
 * that finishes after it fails with an abort, so an old session's result never shows.
 */
export class SharedReads<V> {
  private readonly entries = new Map<string, Entry<V>>();
  private readonly loadedListeners = new Map<string, Set<() => void>>();
  private generation = 0;

  constructor(private readonly capacity: number) {}

  read(key: string, start: (signal: AbortSignal) => Promise<V>, signal?: AbortSignal): Promise<V> {
    const entry = this.entries.get(key) ?? this.start(key, start);
    // Most recently read last; the read just asked for stays.
    this.entries.delete(key);
    this.entries.set(key, entry);
    this.trim(key);
    entry.waiters += 1;
    if (signal === undefined) {
      return entry.promise;
    }
    return untilAborted(entry.promise, signal, () => {
      entry.waiters -= 1;
      if (entry.waiters === 0 && !entry.settled) {
        entry.controller.abort();
        this.forget(key, entry);
      }
    });
  }

  /** Call `listener` whenever a read of `key` succeeds; returns the way to stop. */
  watch(key: string, listener: () => void): () => void {
    const listeners = this.loadedListeners.get(key) ?? new Set();
    listeners.add(listener);
    this.loadedListeners.set(key, listeners);
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) {
        this.loadedListeners.delete(key);
      }
    };
  }

  clear(): void {
    this.generation += 1;
    for (const entry of this.entries.values()) {
      if (!entry.settled) {
        entry.controller.abort();
      }
    }
    this.entries.clear();
  }

  private start(key: string, start: (signal: AbortSignal) => Promise<V>): Entry<V> {
    const generation = this.generation;
    const controller = new AbortController();
    const promise = start(controller.signal).then((value) => {
      if (this.generation !== generation) {
        throw abortError();
      }
      return value;
    });
    const entry: Entry<V> = { promise, controller, waiters: 0, settled: false };
    void promise.then(
      () => {
        entry.settled = true;
        this.trim(null);
        this.loadedListeners.get(key)?.forEach((listener) => { listener(); });
      },
      () => {
        entry.settled = true;
        this.forget(key, entry);
      },
    );
    return entry;
  }

  /** Drop the least recently read finished reads beyond `capacity`, except the one just asked for. */
  private trim(keep: string | null): void {
    let finished = [...this.entries.values()].filter((entry) => entry.settled).length;
    for (const [key, entry] of this.entries) {
      if (finished <= this.capacity) {
        return;
      }
      if (entry.settled && key !== keep) {
        this.entries.delete(key);
        finished -= 1;
      }
    }
  }

  private forget(key: string, entry: Entry<V>): void {
    if (this.entries.get(key) === entry) {
      this.entries.delete(key);
    }
  }
}

function abortError(): DOMException {
  return new DOMException("The read was dropped.", "AbortError");
}

/** The shared read, or the caller's abort, whichever comes first; `leave` runs on the abort. */
function untilAborted<T>(pending: Promise<T>, signal: AbortSignal, leave: () => void): Promise<T> {
  let stopWaiting = () => undefined;
  const aborted = new Promise<never>((_resolve, reject) => {
    stopWaiting = () => {
      leave();
      reject(signal.reason as Error);
    };
    if (signal.aborted) {
      stopWaiting();
    } else {
      signal.addEventListener("abort", stopWaiting, { once: true });
    }
  });
  return Promise.race([pending, aborted]).finally(() => { signal.removeEventListener("abort", stopWaiting); });
}
